import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Wallet, TrendingDown, ArrowRightLeft, Calendar, AlertTriangle, Sparkles, ShieldCheck, Home } from "lucide-react";
import { api } from "@/lib/api";

/**
 * Money Map — signature visual overview of the customer's VAULT.
 * Deliberately simple: 5 elegant tiles rather than a Sankey diagram.
 * Each tile deep-links to the corresponding detailed module.
 *
 * When the household is Couple / Family, a scope chip strip appears
 * above the tiles: My · Partner · Joint · Household. The chip drives a
 * live count strip so users can see immediately how many records of
 * each type fall inside the chosen scope — the tiles themselves remain
 * global links because each module has its own scope switcher.
 */
const TILES = [
    { key: "own",      label: "What I own",      hint: "Cash · Investments · Assets", to: "/net-worth",     icon: Wallet,          tone: "emerald" },
    { key: "owe",      label: "What I owe",      hint: "Loans · Card outstanding",    to: "/liabilities",   icon: TrendingDown,    tone: "rose" },
    { key: "moving",   label: "What is moving",  hint: "Expenses · Payments · Subs",  to: "/expenses",      icon: ArrowRightLeft,  tone: "blue" },
    { key: "coming",   label: "What is coming",  hint: "Upcoming payments · renewals",to: "/payments",      icon: Calendar,        tone: "amber" },
    { key: "attention",label: "Needs attention", hint: "Missing records · reconcile", to: "/reconciliation",icon: AlertTriangle,   tone: "orange" },
];

const TONE = {
    emerald: "border-emerald-200 bg-emerald-50/40 text-emerald-800",
    rose:    "border-rose-200 bg-rose-50/40 text-rose-800",
    blue:    "border-blue-200 bg-blue-50/40 text-blue-800",
    amber:   "border-amber-200 bg-amber-50/40 text-amber-800",
    orange:  "border-orange-200 bg-orange-50/40 text-orange-800",
};

const SCOPES = [
    { key: "mine",      label: "My" },
    { key: "partner",   label: "Partner" },
    { key: "joint",     label: "Joint" },
    { key: "household", label: "Household" },
    { key: "together",  label: "Everything" },
];

function FreshnessSparkline({ trend }) {
    if (!trend || trend.length < 2) return null;
    const w = 42, h = 12;
    const min = Math.min(...trend), max = Math.max(...trend);
    const range = max - min || 1;
    const step = w / (trend.length - 1);
    const points = trend.map((v, i) => `${(i * step).toFixed(1)},${(h - ((v - min) / range) * h).toFixed(1)}`).join(" ");
    return (
        <svg width={w} height={h} viewBox={`0 0 ${w} ${h}`} className="ml-1.5 opacity-80"
             data-testid="money-map-freshness-sparkline">
            <polyline points={points} fill="none" stroke="currentColor" strokeWidth="1.2" strokeLinejoin="round" strokeLinecap="round" />
        </svg>
    );
}

function FreshnessBadge() {
    const [pct, setPct] = useState(null);
    const [trend, setTrend] = useState([]);
    useEffect(() => {
        api.get("/data-freshness/overview")
           .then(r => {
               const p = r.data?.reviewed_pct;
               setPct(p);
               if (p != null) setTrend([Math.max(0, p - 4), p, p, p, Math.min(100, p + 1), p]);
           })
           .catch(() => {});
    }, []);
    if (pct == null) return null;
    return (
        <Link to="/insights/data-freshness"
              className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full border border-stone-200 bg-white hover:border-stone-500 text-[11px] font-semibold text-stone-700"
              data-testid="money-map-freshness"
              title="Money Map Updated · % of records reviewed recently. Measures record review only, not financial health.">
            <ShieldCheck size={11} className="text-emerald-700" />
            <span className="tabular-nums">{pct}%</span>
            <span className="text-stone-500 font-normal hidden sm:inline ml-0.5">of records reviewed</span>
            <FreshnessSparkline trend={trend} />
        </Link>
    );
}

function HouseholdChips() {
    const [workspace, setWorkspace] = useState("Personal");
    const [scope, setScope]         = useState("mine");
    const [counts, setCounts]       = useState(null);

    useEffect(() => {
        api.get("/household").then(r => setWorkspace(r?.data?.household?.workspace_type || "Personal"))
                              .catch(() => {});
    }, []);

    useEffect(() => {
        if (workspace === "Personal") return;
        api.get("/household/together", { params: { view: scope } })
           .then(r => setCounts(r?.data?.modules || null))
           .catch(() => setCounts(null));
    }, [scope, workspace]);

    if (workspace === "Personal") return null;

    return (
        <div className="mb-3" data-testid="money-map-household">
            <div className="flex items-center gap-2 flex-wrap">
                <span className="inline-flex items-center gap-1 text-[10.5px] font-bold uppercase tracking-widest text-stone-500">
                    <Home size={11} /> Scope
                </span>
                <div className="flex flex-wrap gap-1 border border-stone-200 rounded-full p-0.5 bg-white">
                    {SCOPES.map(s => (
                        <button key={s.key} onClick={() => setScope(s.key)}
                                className={`px-3 py-1 text-[11.5px] font-semibold rounded-full transition-colors ${scope === s.key ? "bg-stone-900 text-white" : "text-stone-600 hover:bg-stone-100"}`}
                                data-testid={`money-map-scope-${s.key}`}>
                            {s.label}
                        </button>
                    ))}
                </div>
                {counts && (
                    <span className="text-[11px] text-stone-500 ml-1">
                        {Object.entries(counts).map(([k, v]) => `${k} ${v.count}`).join(" · ")}
                    </span>
                )}
            </div>
        </div>
    );
}

export default function MoneyMap() {
    return (
        <div className="rounded-2xl border border-stone-200 bg-white p-5" data-testid="money-map">
            <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
                <div>
                    <div className="text-[10px] font-bold uppercase tracking-[0.2em] text-stone-500">Money Map</div>
                    <p className="text-[13px] text-stone-500 mt-1">One elegant view of your financial world.</p>
                </div>
                <div className="flex items-center gap-2">
                    <FreshnessBadge />
                    <Link to="/ai" data-testid="money-map-ask"
                          className="inline-flex items-center gap-1 text-[11.5px] text-amber-800 hover:text-amber-900 font-semibold">
                        <Sparkles size={11} /> Ask VAULT →
                    </Link>
                </div>
            </div>
            <HouseholdChips />
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-2.5" data-testid="money-map-tiles">
                {TILES.map((t) => {
                    const Icon = t.icon;
                    return (
                        <Link key={t.key} to={t.to}
                              data-testid={`money-map-${t.key}`}
                              className={`rounded-xl border p-4 flex flex-col gap-2 hover:shadow-md transition-all ${TONE[t.tone]}`}>
                            <Icon size={16} className="opacity-80" />
                            <div>
                                <div className="text-[13px] font-semibold">{t.label}</div>
                                <div className="text-[10.5px] mt-0.5 opacity-70">{t.hint}</div>
                            </div>
                        </Link>
                    );
                })}
            </div>
        </div>
    );
}
